/**
 * As opções dos filtros de menção, carregadas do catálogo vivo.
 *
 * **Carrega uma vez, na primeira abertura do painel.** São seis
 * requisições de catálogo; sem cache elas sairiam a cada vez que
 * alguém abrisse o painel, e ninguém abre uma vez só. Não há lib de
 * fetch neste front (decisão registrada no CLAUDE.md), então o cache é
 * estado da tela — some ao trocar de rota, que é o momento certo: o
 * catálogo é por empresa.
 *
 * **Dedupe por NOME em tema e porta-voz.** Os dois são cadastrados POR
 * MARCA: uma empresa com três marcas tem "Sustentabilidade" três
 * vezes, com ids diferentes. Mostrar as três seria uma lista de
 * opções repetidas em que a pessoa escolhe no escuro. Decisão da dona
 * (2026-08-27): o NOME é a identidade — uma opção só, que manda os N
 * ids por baixo. É o mesmo raciocínio do breakdown do dashboard, que
 * agrupa por nome congelado.
 *
 * Por isso o `value` da opção não é um id: é o nome, e o mapa ao lado
 * traduz nome → ids na hora de montar a query.
 *
 * **O catálogo segue o ESCOPO da tela** (2026-10-05). Tema, grupo-tema e
 * porta-voz são cadastro de CADA empresa — o concorrente tem o dele —,
 * então com "Menções de: Concorrentes" ou "Todas" as opções são a
 * UNIÃO dos catálogos das empresas que o escopo alcança, e o mesmo nome
 * em duas delas continua sendo uma opção que manda os ids das duas. Até
 * então a tela oferecia só o catálogo da empresa em qualquer escopo: o
 * tema do concorrente não era escolhível, e o recorte levado pra
 * "Fechar amostra" carregava id de tema da empresa num relatório de
 * concorrente, que o servidor recusa com 404. Grupo de publicador,
 * porte e canal NÃO mudam: são lista única do cliente, consumida pelas
 * marcas dele e dos concorrentes.
 */

import {
  apiClient,
  type Channel,
  type MentionScope
} from "@/shared/api/api-client";
import type { MultiSelectOption } from "@/shared/components/MultiSelectFilter";

/** Uma dimensão de catálogo vivo: o que a tela mostra e o que o
 *  servidor recebe são coisas diferentes. */
export type NamedCatalog = {
	options: Array<MultiSelectOption<string>>;
	/** nome → todos os ids que carregam esse nome. */
	idsByName: Map<string, number[]>;
	/** id → nome atual. É o caminho de VOLTA do filtro salvo (v3, por
	 *  id): o rótulo gravado é foto, e o chip mostra o nome de hoje. */
	nameById: Map<number, string>;
};

export type FilterOptions = {
	themes: NamedCatalog;
	themeGroups: NamedCatalog;
	/** nome do tema → nomes dos grupos a que ele pertence (um nome de
	 *  tema pode valer várias linhas, e cada linha tem o seu grupo). É
	 *  o que faz "limpar o grupo" derrubar os temas dele
	 *  (`dropThemesOfGroups`). Tema sem grupo não entra no mapa. */
	groupsByTheme: Map<string, Set<string>>;
	spokespersons: NamedCatalog;
	/** Mensagem-chave (2026-11-01): cadastro por marca, como porta-voz — o
	 *  nome da opção é o TEXTO da mensagem, e homônimas entre marcas são
	 *  uma opção só. */
	keyMessages: NamedCatalog;
	publisherGroups: NamedCatalog;
	/** Faixas de porte do publicador da empresa, por ID (2026-11-03): o
	 *  nome é só o rótulo. A lista é inteira e pequena. */
	influencerTypes: NamedCatalog;
	/** Canais de REDE (o cadastro de canais também tem imprensa, e esta
	 *  dimensão recorta só a rede), por ID (2026-11-03). A lista é a
	 *  primeira página e CRESCE por busca (`mergeNamedCatalog`): o nome →
	 *  ids que a consulta usa vive aqui, e um nome que a pessoa marcou
	 *  veio de uma busca que o pôs neste mapa. */
	channels: NamedCatalog;
	/** Veículos que a tela JÁ VIU: nasce VAZIO e CRESCE a cada busca
	 *  (`mergeNamedCatalog`) — são ~14 mil numa empresa do legado, então
	 *  não há carga inteira. O nome → ids que a consulta usa vive aqui:
	 *  um nome que a pessoa marcou veio de uma busca, e a busca o pôs
	 *  neste mapa. */
	vehicles: NamedCatalog;
	/** As clipadoras das menções da empresa (2026-10-31): lista curta, do
	 *  servidor, com "Legado (migrado)" quando há post migrado. */
	clippingServices: Array<MultiSelectOption<string>>;
	/** Quantos canais de rede o cadastro tem NO TOTAL. */
	channelsTotal: number;
	/** "Editado por": `value` é o `sub` do token, `label` é a foto do
	 *  nome — ou o próprio `sub`, quando nenhuma edição trouxe nome. */
	editors: Array<MultiSelectOption<string>>;
	/** Quais dimensões não carregaram. Vazio = tudo certo.
	 *
	 *  A falha viaja como DADO e não como rejeição de propósito: a tela
	 *  gateia a consulta em "as opções chegaram" (`optionsReady`), e uma
	 *  promessa rejeitada deixaria esse carimbo pra trás — quem entra com
	 *  filtro salvo de catálogo ficaria com a lista de menções sem nunca
	 *  carregar, sem spinner e sem erro. Resolvendo sempre, a tela mostra
	 *  o que deu e avisa o que faltou. */
	failed: string[];
};

export const EMPTY_NAMED_CATALOG: NamedCatalog = {
	options: [],
	idsByName: new Map(),
	nameById: new Map()
};

export const EMPTY_FILTER_OPTIONS: FilterOptions = {
	themes: EMPTY_NAMED_CATALOG,
	themeGroups: EMPTY_NAMED_CATALOG,
	groupsByTheme: new Map(),
	spokespersons: EMPTY_NAMED_CATALOG,
	keyMessages: EMPTY_NAMED_CATALOG,
	publisherGroups: EMPTY_NAMED_CATALOG,
	influencerTypes: EMPTY_NAMED_CATALOG,
	channels: EMPTY_NAMED_CATALOG,
	clippingServices: [],
	vehicles: EMPTY_NAMED_CATALOG,
	channelsTotal: 0,
	editors: [],
	failed: []
};

/** O mapa tema → grupos, a partir das duas listas do catálogo. Grupo
 *  desconhecido (tema apontando pra grupo que não veio) não entra. */
export function groupsByThemeName(
	themes: Array<{ name: string; theme_group_id?: number | null }>,
	groups: Array<{ id: number; name: string }>
): Map<string, Set<string>> {
	const groupName = new Map(groups.map(group => [group.id, group.name]));
	const result = new Map<string, Set<string>>();
	for (const theme of themes) {
		const name = theme.theme_group_id == null ? undefined : groupName.get(theme.theme_group_id);
		if (!name) continue;
		const atual = result.get(theme.name);
		if (atual) atual.add(name);
		else result.set(theme.name, new Set([name]));
	}
	return result;
}

export function byName(rows: Array<{ id: number; name: string }>): NamedCatalog {
	const idsByName = new Map<string, number[]>();
	const nameById = new Map<number, string>();
	for (const row of rows) {
		const atual = idsByName.get(row.name);
		if (atual) atual.push(row.id);
		else idsByName.set(row.name, [row.id]);
		nameById.set(row.id, row.name);
	}
	return {
		options: Array.from(idsByName.keys())
			.sort((a, b) => a.localeCompare(b, "pt-BR"))
			.map(name => ({ value: name, label: name })),
		idsByName,
		nameById
	};
}

/** As linhas (id, nome) de um catálogo — a forma que `mergeNamedCatalog`
 *  recebe. */
export function catalogRows(catalog: NamedCatalog): Array<{ id: number; name: string }> {
	return Array.from(catalog.nameById, ([id, name]) => ({ id, name }));
}

/** O catálogo mais as linhas novas. Devolve o MESMO objeto quando nada
 *  é novo — o catálogo de veículos entra nas dependências da consulta, e
 *  uma busca que só repete o que já se sabia não pode virar re-render. */
export function mergeNamedCatalog(
	catalog: NamedCatalog,
	rows: Array<{ id: number; name: string }>
): NamedCatalog {
	const novos = rows.filter(row => catalog.nameById.get(row.id) !== row.name);
	if (!novos.length) return catalog;
	const known = Array.from(catalog.nameById, ([id, name]) => ({ id, name }));
	return byName([...known.filter(row => !novos.some(novo => novo.id === row.id)), ...novos]);
}

/** De quem é um catálogo: `null` é a própria empresa, um número é o id
 *  do VÍNCULO de concorrente (o `competitor_id` das rotas de catálogo). */
export type CatalogOwner = number | null;

/** As empresas cujo catálogo vale num escopo da tela. */
export function ownersForScope(
	scope: MentionScope,
	competitorIds: readonly number[]
): CatalogOwner[] {
	if (scope === "competitors") return [...competitorIds];
	if (scope === "all") return [null, ...competitorIds];
	return [null];
}

/** A mesma chamada de catálogo pra cada dono, numa lista só. Falha de
 *  um dono derruba a dimensão inteira (vira `failed`): metade do
 *  catálogo mostrada como se fosse ele todo seria o silêncio que o
 *  `failed` existe pra evitar. */
export async function acrossOwners<T>(
	owners: readonly CatalogOwner[],
	load: (client: typeof apiClient) => Promise<T[]>
): Promise<T[]> {
	const parts = await Promise.all(
		owners.map(owner => load(apiClient.onCompetitor(owner)))
	);
	return parts.flat();
}

/** Os ids que o servidor precisa receber, a partir do que a tela
 *  selecionou. Um nome escolhido pode valer vários ids. */
export function idsFor(catalog: NamedCatalog, selected: Set<string>): number[] {
	const ids: number[] = [];
	for (const name of selected) ids.push(...(catalog.idsByName.get(name) ?? []));
	return ids;
}

/**
 * Carrega tudo em paralelo. Falha de uma dimensão não derruba as
 * outras: filtro é ferramenta secundária, e uma rota de catálogo fora
 * do ar não pode deixar a pessoa sem os filtros que ainda funcionam —
 * mesmo tratamento que a tela já dá ao carregar marcas e uploads.
 *
 * **O que mudou em 2026-09-10**: a falha deixou de ser SILENCIOSA. Uma
 * dimensão que não carrega devolvia lista vazia, indistinguível de
 * "não há temas cadastrados" — e com a cerca de requisições isso deixou
 * de ser hipótese, porque esta função dispara seis chamadas de uma vez.
 * Agora ela sai em `failed` e a tela avisa. A função continua RESOLVENDO
 * sempre: ver o comentário do campo.
 */
export async function loadFilterOptions(
	token: string,
	scope: MentionScope = "own",
	competitorIds: readonly number[] = []
): Promise<FilterOptions> {
	const owners = ownersForScope(scope, competitorIds);
	const failed: string[] = [];
	const ou = <T,>(dimensao: string, fallback: T) => (): T => {
		failed.push(dimensao);
		return fallback;
	};
	const [temas, grupos, porta, mensagens, publi, tiers, canais, editores, clipadoras] = await Promise.all([
		acrossOwners(owners, client => client.listThemes(token)).catch(ou("Subtemas", [])),
		acrossOwners(owners, client => client.listThemeGroups(token)).catch(
			ou("Temas", [])
		),
		acrossOwners(owners, client => client.listSpokespersons(token)).catch(
			ou("Porta-vozes", [])
		),
		acrossOwners(owners, client => client.listKeyMessages(token)).catch(
			ou("Mensagens-chave", [])
		),
		apiClient.listPublisherGroups(token).catch(ou("Grupos de publicador", [])),
		apiClient.listPublisherTiers(token).catch(ou("Portes de publicador", [])),
		// SÓ canais de rede (2026-10-19): a imprensa tem a dimensão
		// Veículo, por id e com busca no servidor. A primeira página é o
		// que a caixa mostra sem texto; digitar busca o resto.
		apiClient
			.listChannels(token, { postSource: "network", size: 200 })
			.catch(
				ou("Canais", { items: [] as Channel[], total: 0, page: 1, size: 200 })
			),
		// Quem já corrigiu classificação — não é catálogo, é trilha.
		apiClient.listMentionEditors(token, scope).catch(ou("Editores", [])),
		// Lista curta e da EMPRESA (não por escopo): a clipadora é do upload.
		apiClient.clippingServices(token).catch(ou("Clipadoras", []))
	]);

	return {
		failed,
		themes: byName(temas),
		themeGroups: byName(grupos),
		groupsByTheme: groupsByThemeName(temas, grupos),
		spokespersons: byName(porta),
		keyMessages: byName(mensagens.map(row => ({ id: row.id, name: row.message }))),
		publisherGroups: byName(publi),
		editors: editores
			.map(editor => ({ value: editor.id, label: editor.name ?? editor.id }))
			.sort((a, b) => a.label.localeCompare(b.label, "pt-BR")),
		// Porte do publicador é cadastro por empresa (as faixas de
		// seguidores): o filtro viaja pelo ID da faixa, e o nome é rótulo.
		influencerTypes: byName(tiers),
		channels: byName(canais.items),
		clippingServices: clipadoras.map(item => ({ value: item.value, label: item.label })),
		vehicles: EMPTY_NAMED_CATALOG,
		channelsTotal: canais.total
	};
}
